import { FetcherProvider } from "@website/fetcher/provider";
import { useAuthPages } from "@website/hooks/auth-manager";
import { GenerateGrowthBookInstance } from "@website/tracking/tracking.experiment";
import {
  ApplicationNameType,
  mapProductForUserTracking,
  startUserTracking
} from "@website/tracking/user";
import { SnackbarProvider } from "@website/ui/snackbar";
import "@website/ui/styles";
import { getChannel } from "@website/utils/channel/get-channel";
import { isChannel } from "@website/utils/channel/helpers";
import { Channels } from "@website/utils/channel/types";
import { SERVER_NOW } from "@website/utils/server-now";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import { SchemaLocalBusiness } from "@website/widget/scripts/schema-local-business";
import { SchemaOrganization } from "@website/widget/scripts/schema-organization";
import { Scripts } from "@website/widget/scripts/scripts";
import { WelcomeScreenClientHandler } from "@website/widget/welcom-screen-handler";
import toString from "lodash-es/toString";
import NextApp, { AppContext, AppProps } from "next/app";
import Head from "next/head";
import { Suspense } from "react";

GenerateGrowthBookInstance();

startUserTracking({
  product: mapProductForUserTracking(
    process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType
  )
});

const webEngageLicenseCode = {
  android: "11b5650c0",
  ios: "11b5650c0",
  jek: "~47b6574d",
  ap: "11b5650c0",
  app: "11b5650c0",
  ads: "11b5650c0",
  "snapp-ir": "~47b6574d",
  snapp: "~47b6574d",
  website: "11b5650c0",
  telesale: "11b5650c0"
}[getChannel() as string];

const gtmLicenseCode = {
  android: "GTM-MT54VMN",
  ios: "GTM-KJPQFH4",
  jek: "GTM-P8WGVRK",
  ap: "GTM-KJPQFH4",
  app: "GTM-KJPQFH4",
  ads: "GTM-KJPQFH4",
  "snapp-ir": "GTM-5HTF8BD",
  snapp: "GTM-P8WGVRK",
  website: "GTM-MT54VMN",
  telesale: "GTM-MT54VMN"
}[getChannel() as string];

const indexedChannels: Record<string, Array<Channels>> = {
  checkout: [],
  villa: ["website", "web", "jek"],
  tour: [],
  visa: ["website", "web", "jek", "snapp-ir"],
  accommodation: [],
  "hotel-international": [],
  "hotel-domestic": ["website", "web", "jek", "snapp-ir"]
};

export const productsToNotFollow = ["tour", "hotel-international"];
const shouldNotFollowPage = productsToNotFollow.includes(
  process?.env?.NEXT_PUBLIC_APP_NAME as string
);

export const App = ({ Component, pageProps }: AppProps) => {
  useAuthPages();
  const { title, description, fallbackTitle, resolvedUrl } =
    pageProps?.seoData ?? {};

  const basicTitle = replaceSnappTripBasedOnChannel("اسنپ تریپ");
  const finalTitle =
    title || `${fallbackTitle ? `${fallbackTitle} | ` : ""}${basicTitle}`;
  const finalDescription = description || finalTitle;

  const metaProperties = [
    { name: "twitter:url", content: resolvedUrl },
    { name: "twitter:title", content: finalTitle },
    { name: "twitter:description", content: finalDescription },
    {
      property: "og:image",
      content: "/favicon.ico"
    },
    {
      property: "og:image:alt",
      content: `لوگوی ${basicTitle}`
    },
    { property: "og:locale", content: "fa_IR" },
    { property: "og:site_name", content: basicTitle },
    { property: "og:type", content: "website" },
    { property: "og:url", content: resolvedUrl },
    { property: "og:description", content: finalDescription },
    { property: "og:title", content: finalTitle }
  ];

  return (
    <>
      <Head>
        <title>{finalTitle}</title>
        <meta content={finalDescription} name="description" />
        {resolvedUrl && <link href={resolvedUrl} rel="canonical" />}
        {metaProperties.map((properties) => (
          <meta key={properties.name || properties.property} {...properties} />
        ))}
        {shouldNotFollowPage ? (
          <meta content="noindex,nofollow" name="robots" />
        ) : pageProps?.seoData?.robots ? (
          <meta content={pageProps?.seoData?.robots} name="robots" />
        ) : (
          !isChannel(
            indexedChannels[toString(process?.env?.NEXT_PUBLIC_APP_NAME)]
          ) && <meta content="noindex,follow" name="robots" />
        )}
        <meta name="google" content="notranslate" />
      </Head>
      <FetcherProvider>
        <Suspense fallback={null}>
          <WelcomeScreenClientHandler />
        </Suspense>
        <SnackbarProvider duration="long">
          <Component {...pageProps} />
        </SnackbarProvider>
      </FetcherProvider>
      <SchemaOrganization />
      <SchemaLocalBusiness />
      <Scripts
        gtmLicence={gtmLicenseCode}
        serverNow={pageProps.serverNow}
        webengageLicence={webEngageLicenseCode}
      />
    </>
  );
};

App.getInitialProps = async (
  appContext: AppContext
): Promise<AppProps["pageProps"] & { pageProps: { serverNow: number } }> => {
  const appProperties = await NextApp.getInitialProps(appContext);

  return {
    ...appProperties,
    pageProps: {
      ...appProperties.pageProps,
      serverNow: SERVER_NOW()
    }
  };
};
